import axios, { AxiosError, AxiosHeaders } from "axios";
import { appConfig } from "@/config";
import { normalizeHttpError, restoreBlobErrorBody } from "./httpError";
import { API_TIMEOUT_MS, installRetry } from "./resilience";
import { installSimulatedLatency } from "./simulatedLatency";

export type SessionInvalidatedListener = () => void;

let apiJWT: string | null = null;
const sessionInvalidatedListeners = new Set<SessionInvalidatedListener>();

export const apiClient = axios.create({
  baseURL: appConfig.apiBaseUrl,
  timeout: API_TIMEOUT_MS,
  headers: {
    "Content-Type": "application/json",
  },
  // 배열 쿼리 파라미터는 brackets 없이 키를 반복한다(`?k=a&k=b`). 이 형식은 FastAPI 의
  // `list[...] = Query(...)` 가 받는 형식이다. axios 기본값(`k[]=a`)은 그 alias 와 맞지 않는다.
  // 스칼라 파라미터는 바꾸지 않는다.
  paramsSerializer: { indexes: null },
});

installSimulatedLatency(apiClient);
// 재시도는 정규화/401 인터셉터보다 먼저 등록한다. 그래야 원본 AxiosError를 먼저 받아 다시 요청한다.
installRetry(apiClient);

apiClient.interceptors.request.use((config) => {
  if (apiJWT === null) {
    return config;
  }

  const headers = AxiosHeaders.from(config.headers);
  headers.set("Authorization", `Bearer ${apiJWT}`);
  config.headers = headers;
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    // blob 요청(frame.png 등)의 에러 본문을 먼저 텍스트로 바꾼다. 그래야 아래 정규화가 서버 code 를 읽는다.
    await restoreBlobErrorBody(error);
    // 지금 설치된 토큰이 401로 거부된 경우에만 세션을 무효 처리한다. 토큰을 바꾸면, 바꾸기 직전에
    // 보낸 요청이 이전 토큰으로 401을 받아 늦게 돌아올 수 있다. 그 응답으로 방금 시작한 세션을
    // 끊으면 안 된다.
    if (apiJWT !== null && isUnauthorizedError(error) && wasSentWithCurrentToken(error)) {
      apiJWT = null;
      notifySessionInvalidated();
    }
    return Promise.reject(normalizeHttpError(error));
  },
);

export function setJWT(jwt: string): void {
  apiJWT = jwt;
}

export function clearJWT(): void {
  apiJWT = null;
}

export function onSessionInvalidated(listener: SessionInvalidatedListener): () => void {
  sessionInvalidatedListeners.add(listener);
  return () => {
    sessionInvalidatedListeners.delete(listener);
  };
}

function isUnauthorizedError(error: unknown): error is AxiosError {
  return error instanceof AxiosError && error.response?.status === 401;
}

// 그 요청이 실제로 보낸 토큰이 지금 설치된 토큰인지 판정한다. 재시도(resilience)는 다시 요청할
// 때 요청 인터셉터를 다시 실행한다. 그래서 config 의 헤더는 마지막 시도가 보낸 값이다.
function wasSentWithCurrentToken(error: AxiosError): boolean {
  const sent = AxiosHeaders.from(error.config?.headers).get("Authorization");
  // 헤더를 읽을 수 없으면(요청 인터셉터 이전에 실패) 판단 근거가 없다. 그래서 현재 세션 문제로 본다.
  return typeof sent !== "string" || sent === `Bearer ${apiJWT}`;
}

function notifySessionInvalidated(): void {
  for (const listener of sessionInvalidatedListeners) {
    try {
      listener();
    } catch {
      // 한 listener 의 오류가 다른 리스너의 호출을 막지 않게 한다.
    }
  }
}
